import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const CUSTOM = '__custom';
const NONE = '__none';
const ANY = '__any';
const blankLine = () => ({ description_id: '', description: '', amount: 0, cost_centre_id: '' });

export const BulkApplyDialog = ({ open, onClose, meta, onDone }) => {
  const [lines, setLines] = useState([blankLine()]);
  const [department, setDepartment] = useState('');
  const [visaCompany, setVisaCompany] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setLines([blankLine()]); setDepartment(''); setVisaCompany(''); setPreview(null); } }, [open]);

  const payload = () => ({
    items: lines.filter((l) => l.description.trim()).map((l) => ({ ...l, amount: Number(l.amount) || 0, description: l.description.trim() })),
    department, visa_company: visaCompany,
  });
  useEffect(() => {
    if (!open || !(department || visaCompany)) { setPreview(null); return; }
    api.post('/api/visa-costs/bulk-apply', { ...payload(), items: [{ description: 'x', amount: 0 }], preview: true })
      .then((r) => setPreview(r.data)).catch(() => setPreview(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, department, visaCompany]);

  const upd = (i, patch) => setLines(lines.map((l, n) => (n === i ? { ...l, ...patch } : l)));
  const pick = (i, id) => {
    if (id === CUSTOM) return upd(i, { description_id: '', description: '' });
    const d = meta.descriptions.find((x) => x.id === id);
    upd(i, { description_id: id, description: d.name, amount: d.default_amount, cost_centre_id: d.cost_centre_id || '' });
  };

  const apply = async () => {
    const p = payload();
    if (!p.items.length) { toast({ title: 'Add at least one cost line', variant: 'destructive' }); return; }
    if (!window.confirm(`Apply ${p.items.length} line(s) to ${preview?.count || 0} employee(s)? Lines with the same description are replaced; other lines are kept.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/api/visa-costs/bulk-apply', p);
      toast({ title: 'Template applied', description: `Updated ${data.count} employee(s). Staff Cost synced.` });
      onDone();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vc-bulk-dialog">
        <DialogHeader><DialogTitle>Bulk cost template</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Department</Label>
            <Select value={department || ANY} onValueChange={(v) => setDepartment(v === ANY ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="vc-bulk-department"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>Any department</SelectItem>{meta.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Visa company</Label>
            <Select value={visaCompany || ANY} onValueChange={(v) => setVisaCompany(v === ANY ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="vc-bulk-visa-company"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>Any visa company</SelectItem>{(meta.visa_companies || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <p className="text-xs text-gray-600 flex items-center gap-1" data-testid="vc-bulk-preview">
          <Users className="h-3.5 w-3.5" />
          {preview ? <span><b>{preview.count}</b> active employee(s) match{preview.count ? `: ${preview.employees.slice(0, 6).join(', ')}${preview.count > 6 ? '…' : ''}` : ''}</span> : 'Pick a department and/or visa company.'}
        </p>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1.3fr_1fr_110px_1fr_32px] gap-2" data-testid={`vc-bulk-line-${i}`}>
              <Select value={l.description_id || CUSTOM} onValueChange={(v) => pick(i, v)}>
                <SelectTrigger className="h-9" data-testid={`vc-bulk-desc-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={CUSTOM}>Custom description…</SelectItem>{meta.descriptions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="h-9" value={l.description} disabled={!!l.description_id} placeholder="Description" onChange={(e) => upd(i, { description: e.target.value })} data-testid={`vc-bulk-text-${i}`} />
              <Input className="h-9" type="number" min={0} value={l.amount} onChange={(e) => upd(i, { amount: e.target.value })} data-testid={`vc-bulk-amount-${i}`} />
              <Select value={l.cost_centre_id || NONE} onValueChange={(v) => upd(i, { cost_centre_id: v === NONE ? '' : v })}>
                <SelectTrigger className="h-9" data-testid={`vc-bulk-cc-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Unassigned</SelectItem>{meta.cost_centres.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setLines(lines.filter((_, n) => n !== i))} data-testid={`vc-bulk-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setLines([...lines, blankLine()])} data-testid="vc-bulk-add-line"><Plus className="h-3.5 w-3.5 mr-1" />Add line</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} disabled={busy || !preview?.count} data-testid="vc-bulk-apply">{busy ? 'Applying…' : `Apply to ${preview?.count || 0} employee(s)`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
